import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";

const API_BASE_URL = "https://api.checkmark.mx/";
const ACCESS_TOKEN = "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3";

const COLUMNAS = [
  "Source.Name", "Cantidad", "Grupo", "Razon Social", "Direccion", "Agencia",
  "Departamento", "Fecha", "Dia", "Mes", "Año", "Asesor", "Cliente", "Fuente",
  "Solicitud en Piso", "Telefono", "Estatus", "Sub estatus", "Financiera",
  "Fecha de formalizacion", "Dia2", "Mes2", "Año2", "Viable para Multimarca",
  "$Accesorios", "Edega", "Carline", "Comentarios", "NR Finance", "Banorte",
  "Scotiabank", "Santander", "Cibanco", "Sicrea", "BBVA", "Inbursa", "Hey Banco", "HSBC",
  "Cetelem", "VWSF", "Seat SF", "Cupra SF", "Kuna", "Mstar", "Credito GO",
  "Finsus", "Nexu", "TipAuto",
];

// Mapa de nombre de financiera (normalizado) -> columna del Excel.
const FINANCIERA_NORMALIZADA_A_COLUMNA = {
  nrfinance: "NR Finance",
  nr: "NR Finance",
  banorte: "Banorte",
  scotiabank: "Scotiabank",
  santander: "Santander",
  cibanco: "Cibanco",
  sicrea: "Sicrea",
  bbva: "BBVA",
  inbursa: "Inbursa",
  heybanco: "Hey Banco",
  hsbc: "HSBC",
  cetelem: "Cetelem",
  vwfs: "VWSF",
  vwsf: "VWSF",
  volkswagen: "VWSF",
  seatsf: "Seat SF",
  seat: "Seat SF",
  cuprasf: "Cupra SF",
  cupra: "Cupra SF",
  kuna: "Kuna",
  mstar: "Mstar",
  creditogo: "Credito GO",
  // FinanMadrid cambió de nombre a Finsus (la columna en BD sigue siendo finanmadrid)
  finanmadrid: "Finsus",
  finsus: "Finsus",
  nexu: "Nexu",
  tipauto: "TipAuto",
};

// --- helpers de transformación (mapeo a columnas del Excel) ---

function normalizarTexto(str) {
  return String(str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "");
}

function capitalizar(str) {
  if (!str) return "";
  const limpio = String(str).trim().toLowerCase();
  return limpio.charAt(0).toUpperCase() + limpio.slice(1);
}

// Homologa Sí/No a "Si"/"No" (capitalizado), sin importar acentos/mayúsculas de origen.
// Usado para "Solicitud en Piso" (viene de venta_babilonia).
function homologarSiNoCapitalizado(value) {
  const raw = normalizarTexto(value);
  if (raw === "si") return "Si";
  if (raw === "no") return "No";
  return "";
}

// Homologa Sí/No a "SI"/"NO" (todo mayúsculas). Usado para "Viable para Multimarca".
function homologarSiNoMayusculas(value) {
  const raw = normalizarTexto(value);
  if (raw === "si") return "SI";
  if (raw === "no") return "NO";
  return "";
}

// Homologa cualquier texto a mayúsculas. Usado para "Fuente".
function homologarMayusculas(value) {
  if (!value) return "";
  return String(value).trim().toUpperCase();
}

// Homologa tipo_auto a "Nuevos"/"Seminuevos" sin importar si viene singular,
// plural, o con mayúsculas distintas ("Nuevo", "nuevos", "Seminuevo", etc).
function homologarDepartamento(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw.includes("semi")) return "Seminuevos";
  if (raw.includes("nuevo")) return "Nuevos";
  return capitalizar(value); // valor no reconocido: se deja tal cual, solo capitalizado
}

// Parser robusto para campos JSON de la BD que a veces vienen doble-codificados
// (un string que contiene un string JSON adentro, con comillas escapadas).
function parseJsonRobusto(str) {
  if (!str) return null;
  try {
    let parsed = JSON.parse(str);
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

// Busca recursivamente un brand:"all" (sin importar mayúsculas) en cualquier nivel
// de anidamiento -- necesario porque planes_seleccionados no siempre tiene la misma
// forma: a veces es una lista de "planes" (Elite/Gold/Platinum con accessories[]
// adentro) y otras veces es una lista plana de accesorios individuales con brand
// directo. La búsqueda recursiva cubre ambos casos sin depender de la forma exacta.
function contieneBrandAll(nodo) {
  if (Array.isArray(nodo)) {
    return nodo.some((item) => contieneBrandAll(item));
  }
  if (nodo && typeof nodo === "object") {
    if (typeof nodo.brand === "string" && nodo.brand.toLowerCase() === "all") {
      return true;
    }
    return Object.values(nodo).some((valor) => contieneBrandAll(valor));
  }
  return false;
}

function getEdegaValor(planesSeleccionadosStr) {
  const planes = parseJsonRobusto(planesSeleccionadosStr);
  if (!planes) return "NO";
  return contieneBrandAll(planes) ? "SI" : "NO";
}

function splitFecha(fechaStr) {
  if (!fechaStr) return { fecha: "", dia: "", mes: "", anio: "" };
  const datePart = fechaStr.split(" ")[0];
  const [anio, mes, dia] = datePart.split("-").map(Number);
  if (!anio || !mes || !dia) return { fecha: "", dia: "", mes: "", anio: "" };
  const pad = (n) => String(n).padStart(2, "0");
  return { fecha: `${pad(dia)}/${pad(mes)}/${anio}`, dia, mes, anio };
}

function getClienteNombre(row) {
  return [row.nombre, row.apellido_paterno, row.apellido_materno]
    .filter(Boolean)
    .join(" ")
    .trim();
}

function getAccesoriosTotal(planesSeleccionadosStr) {
  const planes = parseJsonRobusto(planesSeleccionadosStr);
  if (!Array.isArray(planes)) return "";
  const seleccionado = planes.find((p) => p.isSelected === true);
  return seleccionado ? seleccionado.totalPrice : "";
}

function getComentariosTexto(comentariosStr) {
  if (!comentariosStr) return "";
  try {
    const arr = JSON.parse(comentariosStr);
    if (!Array.isArray(arr)) return "";
    return arr
      .map((c) => `${c.comentario || ""} (${c.nombre_asesor || ""}, ${c.fecha || ""})`)
      .join(" | ");
  } catch (e) {
    return typeof comentariosStr === "string" ? comentariosStr : "";
  }
}

function getFechaFormalizacion(historialCambiosStatusStr) {
  if (!historialCambiosStatusStr) return "";
  try {
    const arr = JSON.parse(historialCambiosStatusStr);
    if (!Array.isArray(arr)) return "";

    const eventosFormalizado = arr.filter(
      (ev) =>
        (ev.campo === "status" || ev.campo === "sub_status") &&
        ev.valor_nuevo === "Formalizado" &&
        ev.fecha
    );

    if (eventosFormalizado.length === 0) return "";

    eventosFormalizado.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
    return eventosFormalizado[0].fecha;
  } catch (e) {
    return "";
  }
}

function getFinancierasMarcadas(financieraInicial, historialCambiosStr) {
  const marcadas = new Set();

  const agregar = (nombreFinanciera) => {
    const normalizado = normalizarTexto(nombreFinanciera);
    const columna = FINANCIERA_NORMALIZADA_A_COLUMNA[normalizado];
    if (columna) marcadas.add(columna);
  };

  if (financieraInicial) agregar(financieraInicial);

  if (historialCambiosStr) {
    try {
      const arr = JSON.parse(historialCambiosStr);
      if (Array.isArray(arr)) {
        arr
          .filter((ev) => ev.campo === "financiera" && ev.financiera)
          .forEach((ev) => agregar(ev.financiera));
      }
    } catch (e) {
      // historial_cambios no es JSON válido: se ignora, ya se consideró financiera inicial
    }
  }

  return marcadas;
}

function flagEn(set, columna) {
  return set.has(columna) ? 1 : "";
}

// Catálogo de Grupo/Razón Social/Dirección/nombre homologado de Agencia, por ag_id
// (ag_id es la clave confiable; el texto de "agencia" en la BD puede variar).
// Fuente: listado que compartió el usuario cruzado contra la imagen de grupos,
// alineado por posición vertical (confirmado por el usuario). Solo quedan
// pendientes las 4 agencias que no aparecen en la imagen (ver bloque final).
const AGENCIA_CATALOGO = {
  // --- Confiable (1 Razón Social con sus agencias exactas, sin ambigüedad) ---
  "ag0532_5598bb1c503e6": { agencia: "Nissan Aragon", grupo: "Gocar", razonSocial: "Spanicar Mexico", direccion: "Gabriel Muñoz" },
  "ag0532_5598bb1c86d5a": { agencia: "Nissan Santa Clara", grupo: "Gocar", razonSocial: "Spanicar Mexico", direccion: "Gabriel Muñoz" },
  "ag5713_5c33d949e445e": { agencia: "Nissan Texcoco", grupo: "Gocar", razonSocial: "Spanicar Mexico", direccion: "Gabriel Muñoz" },
  "ag5912_5c33d9c0c10df": { agencia: "Nissan Tecamac", grupo: "Gocar", razonSocial: "Spanicar Mexico", direccion: "Gabriel Muñoz" },
  "ag0533_5598bb1d621ab": { agencia: "Renault Universidad", grupo: "Gocar", razonSocial: "Euro France", direccion: "Jose Gomez Aguilar" },
  "ag0532_5598bb1c4042b": { agencia: "Renault San Angel", grupo: "Gocar", razonSocial: "Euro France", direccion: "Jose Gomez Aguilar" },

  // --- Confirmado por el usuario: Transace/Gocar Oriente alineados por posición
  // (Transace -> MG Ecatepec, Gocar Oriente -> Chirey Ecatepec).
  "ag1150_5b44ccb6362b8": { agencia: "MG Ecatepec", grupo: "Gocar", razonSocial: "Transace", direccion: "Edgar Pacheco" },
  "ag4931_691e032bd8757": { agencia: "MG Ecatepec", grupo: "Gocar", razonSocial: "Transace", direccion: "Edgar Pacheco" },
  "ag3307_631f6d5397531": { agencia: "Chirey Ecatepec", grupo: "Gocar", razonSocial: "Gocar Oriente", direccion: "Edgar Pacheco" },

  // --- Confirmado por alineación de posición (mismo criterio que Transace/Gocar
  // Oriente): Leon Motors (3 filas) -> VW, Seat, Cupra ; Gocar Bajio (1 fila) -> Seminuevos Leon.
  "ag0532_5598bb1c51d78": { agencia: "VW", grupo: "Gocar", razonSocial: "Leon Motors", direccion: "Francisco Tarazona" },
  "ag5348_691e042c59a67": { agencia: "Seat", grupo: "Gocar", razonSocial: "Leon Motors", direccion: "Francisco Tarazona" },
  "ag3651_663021039f3f2": { agencia: "Cupra", grupo: "Gocar", razonSocial: "Leon Motors", direccion: "Francisco Tarazona" },
  "ag0532_5598bb1c56605": { agencia: "Seminuevos Leon", grupo: "Gocar", razonSocial: "Gocar Bajio", direccion: "Francisco Tarazona" },
  "ag0931_6500d38be2bc6": { agencia: "Seminuevos Leon", grupo: "Gocar", razonSocial: "Gocar Bajio", direccion: "Francisco Tarazona" },
  "ag1035_6500d3cb15aed": { agencia: "Seminuevos Leon", grupo: "Gocar", razonSocial: "Gocar Bajio", direccion: "Francisco Tarazona" },

  // --- PENDIENTE: Camino del Sol Baja Sur/Santa Fe/Baja Norte (Sunroad) comparten
  // 5 agencias entre 3 razones sociales, tampoco hay forma de repartirlas 1 a 1.
  // Grupo y Dirección sí son seguros (compartidos por las 3).
  // --- Confirmado por alineación de posición: Camino del Sol Baja Sur (2 filas) ->
  // MG Toluca, MG Metepec ; Camino del sol Santa Fe (2 filas) -> Honda Tollocan,
  // Honda Metepec ; Camino del Sol Baja Norte (1 fila) -> Honda Santa Fe.
  "ag2327_620bfdaf30656": { agencia: "MG Toluca", grupo: "Sunroad", razonSocial: "Camino del Sol Baja Sur", direccion: "Edgar Pacheco" },
  "ag3636_61d5d72451639": { agencia: "MG Metepec", grupo: "Sunroad", razonSocial: "Camino del Sol Baja Sur", direccion: "Edgar Pacheco" },
  "ag5650_5e20f842e21a6": { agencia: "Honda Tollocan", grupo: "Sunroad", razonSocial: "Camino del sol Santa Fe", direccion: "Edgar Pacheco" },
  "ag5457_5e20f7d1c1032": { agencia: "Honda Metepec", grupo: "Sunroad", razonSocial: "Camino del sol Santa Fe", direccion: "Edgar Pacheco" },
  "ag0532_5598bb1c488c9": { agencia: "Honda Santa Fe", grupo: "Sunroad", razonSocial: "Camino del Sol Baja Norte", direccion: "Edgar Pacheco" },

  // --- PENDIENTE: no aparecen en la imagen en absoluto (sin Grupo/Razón Social/Dirección)
  "ag0028_5c33da0cc1c4d": { agencia: "Seminuevos Gocar Azcapotzalco", grupo: "", razonSocial: "", direccion: "" }, // PENDIENTE completo
  "ag3912_5980f500b41b4": { agencia: "Renault Ajusco", grupo: "", razonSocial: "", direccion: "" }, // PENDIENTE completo
  "ag5423_5a8736ef2eeb7": { agencia: "Honda Los Cabos", grupo: "", razonSocial: "", direccion: "" }, // PENDIENTE completo
  "ag5822_5bacfe1e8a7aa": { agencia: "Renault San Ajusco - Nuevos", grupo: "", razonSocial: "", direccion: "" }, // PENDIENTE completo
};

function mapVentaAFila(row) {
  const { fecha, dia, mes, anio } = splitFecha(row.fecha_creacion);

  const fechaFormalizacionRaw = getFechaFormalizacion(row.historial_cambios_status);
  const { fecha: fecha2, dia: dia2, mes: mes2, anio: anio2 } = splitFecha(fechaFormalizacionRaw);

  const financierasMarcadas = getFinancierasMarcadas(row.financiera, row.historial_cambios);

  const catalogo = AGENCIA_CATALOGO[row.ag_id];

  return {
    "Source.Name": "",
    "Cantidad": 1,
    "Grupo": catalogo?.grupo || "",
    "Razon Social": catalogo?.razonSocial || "",
    "Direccion": catalogo?.direccion || "",
    "Agencia": catalogo?.agencia || row.agencia || "(En blanco)",
    "Departamento": normalizeDepartamentoDash(row.tipo_auto),
    "Fecha": fecha,
    "Dia": dia,
    "Mes": mes,
    "Año": anio,
    "Asesor": normalizeTextDash(row.asesor) || "EXTERNO",
    "Cliente": getClienteNombre(row),
    "Fuente": homologarMayusculas(row.fuente) || "DIGITAL",
    "Solicitud en Piso": homologarSiNoCapitalizado(row.venta_babilonia),
    "Telefono": row.telefono || "",
    "Estatus": normalizeStatusDash(row.status),
    "Sub estatus": normalizeSubStatusDash(row.sub_status),
    // Se muestra homologada (ej. FinanMadrid → Finsus, NR → NR Finance)
    "Financiera":
      FINANCIERA_NORMALIZADA_A_COLUMNA[normalizarTexto(row.financiera)] ||
      normalizeTextDash(row.financiera) ||
      "(En blanco)",
    "Fecha de formalizacion": fecha2,
    "Dia2": dia2,
    "Mes2": mes2,
    "Año2": anio2,
    "Viable para Multimarca": homologarSiNoMayusculas(row.multicarma),
    "$Accesorios": getAccesoriosTotal(row.planes_seleccionados),
    "Edega": getEdegaValor(row.planes_seleccionados),
    "Carline": row.modelo_vehiculo || "",
    "Comentarios": getComentariosTexto(row.comentarios),
    "NR Finance": flagEn(financierasMarcadas, "NR Finance"),
    "Banorte": flagEn(financierasMarcadas, "Banorte"),
    "Scotiabank": flagEn(financierasMarcadas, "Scotiabank"),
    "Santander": flagEn(financierasMarcadas, "Santander"),
    "Cibanco": flagEn(financierasMarcadas, "Cibanco"),
    "Sicrea": flagEn(financierasMarcadas, "Sicrea"),
    "BBVA": flagEn(financierasMarcadas, "BBVA"),
    "Inbursa": flagEn(financierasMarcadas, "Inbursa"),
    "Hey Banco": flagEn(financierasMarcadas, "Hey Banco"),
    "HSBC": flagEn(financierasMarcadas, "HSBC"),
    "Cetelem": flagEn(financierasMarcadas, "Cetelem"),
    "VWSF": flagEn(financierasMarcadas, "VWSF"),
    "Seat SF": flagEn(financierasMarcadas, "Seat SF"),
    "Cupra SF": flagEn(financierasMarcadas, "Cupra SF"),
    "Kuna": flagEn(financierasMarcadas, "Kuna"),
    "Mstar": flagEn(financierasMarcadas, "Mstar"),
    "Credito GO": flagEn(financierasMarcadas, "Credito GO"),
    "Finsus": flagEn(financierasMarcadas, "Finsus"),
    "Nexu": flagEn(financierasMarcadas, "Nexu"),
    "TipAuto": flagEn(financierasMarcadas, "TipAuto"),
  };
}

// --- lógica de "Formalizada", copiada tal cual de dashboard-rendimiento.js ---
// para que el conteo del export pueda coincidir exacto con el del dashboard.

function normalizeTextDash(value) {
  return String(value || "").trim();
}

function normalizeStatusDash(value) {
  const raw = normalizeTextDash(value);
  const v = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (!v) return "Sin status";
  if (v === "sin status") return "Sin status";
  if (v === "autorizado") return "Autorizado";
  if (v === "rechazado") return "Rechazado";
  if (v === "pendiente de buro") return "Pendiente de Buro";
  if (v === "pendiente de autorizacion") return "Pendiente de Autorización";
  if (v === "cotizacion") return "Cotización";
  if (v === "formalizado") return "Formalizado";

  return "Sin status";
}

function normalizeSubStatusDash(value) {
  const raw = normalizeTextDash(value);
  const v = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (!v) return "Sin substatus";
  if (v === "sin substatus") return "Sin substatus";
  if (v === "analisis") return "Analisis";
  if (v === "aprobado sin formalizar") return "Aprobado Sin Formalizar";
  if (v === "aprobado sin finalizar") return "Aprobado Sin Formalizar";
  if (v === "condicionado viable") return "Condicionado Viable";
  if (v === "condicionado no viable") return "Condicionado No Viable";
  if (v === "facturado") return "Facturado";
  if (v === "formalizado") return "Formalizado";
  if (v === "incompleto") return "Incompleto";
  if (v === "rechazado") return "Rechazado";

  return "Sin substatus";
}

function normalizeTipoPagoDash(value) {
  const raw = normalizeTextDash(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (raw === "si") return "Contado";
  if (raw === "no") return "Crédito";
  if (raw === "arrendamiento") return "Arrendamiento";

  return "Crédito";
}

// Mismo criterio que normalizeDepartamento() en dashboard-rendimiento.js
function normalizeDepartamentoDash(value) {
  const raw = normalizeTextDash(value).toLowerCase();

  if (raw.includes("semi")) return "Seminuevos";
  if (raw.includes("nuevo")) return "Nuevos";

  return "(En blanco)";
}

// Listas fijas (no vienen del backend): son exactamente las categorías a las
// que normalize*Dash() puede mapear cualquier valor crudo de la BD. Filtrar
// contra estas categorías (en vez de los valores crudos únicos de la BD)
// es lo que garantiza que el export cuente igual que el dashboard.
const CANONICAL_STATUS = [
  "Autorizado",
  "Rechazado",
  "Pendiente de Buro",
  "Pendiente de Autorización",
  "Cotización",
  "Formalizado",
  "Sin status",
];

const CANONICAL_SUB_STATUS = [
  "Analisis",
  "Aprobado Sin Formalizar",
  "Condicionado Viable",
  "Condicionado No Viable",
  "Facturado",
  "Formalizado",
  "Incompleto",
  "Rechazado",
  "Sin substatus",
];

const CANONICAL_DEPARTAMENTO = ["Nuevos", "Seminuevos", "(En blanco)"];

const CANONICAL_TIPO_PAGO = ["Crédito", "Contado", "Arrendamiento"];

// Mismo criterio que isFormalizada() en dashboard-rendimiento.js:
// Crédito -> sub_status === "Formalizado" ; Contado/Arrendamiento -> status === "Formalizado"
function esFormalizadaComoDashboard(row) {
  const status = normalizeStatusDash(row.status);
  const subStatus = normalizeSubStatusDash(row.sub_status);
  const tipoPago = normalizeTipoPagoDash(row.depende_auto);

  const esCredito = tipoPago === "Crédito";
  const esContadoOArrendamiento = tipoPago === "Contado" || tipoPago === "Arrendamiento";

  return (
    (esCredito && subStatus === "Formalizado") ||
    (esContadoOArrendamiento && status === "Formalizado")
  );
}

const MARCADOR_AGENCIA_BLANCO = "__BLANK__";
const ETIQUETA_AGENCIA_BLANCO = "(En blanco)";

function etiquetaOpcion(valor) {
  return valor === MARCADOR_AGENCIA_BLANCO ? ETIQUETA_AGENCIA_BLANCO : valor;
}

function limpiarOpciones(lista) {
  if (!Array.isArray(lista)) return [];
  return Array.from(
    new Set(
      lista
        .map((v) => (typeof v === "string" ? v.trim() : v))
        .filter((v) => v !== null && v !== undefined && v !== "")
    )
  ).sort((a, b) => {
    // El marcador de "(En blanco)" siempre al final, el resto alfabético normal.
    if (a === MARCADOR_AGENCIA_BLANCO) return 1;
    if (b === MARCADOR_AGENCIA_BLANCO) return -1;
    return String(a).localeCompare(String(b), "es");
  });
}

// --- estilos propios, blindados con !important contra el CSS del tema ---
const EstilosExport = () => (
  <style>{`
    .esx-wrap {
      color: #1f2937 !important;
    }
    .esx-wrap h4,
    .esx-wrap h6,
    .esx-wrap label,
    .esx-wrap .form-check-label,
    .esx-wrap .esx-hint,
    .esx-wrap option {
      color: #1f2937 !important;
    }
    .esx-section {
      background: #fff !important;
      border: 1px solid #e5e7eb !important;
      border-radius: 10px;
      padding: 16px 18px;
      margin-bottom: 16px;
    }
    .esx-section-title {
      font-size: 13px !important;
      font-weight: 700 !important;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: #6b7280 !important;
      margin-bottom: 12px !important;
    }
    .esx-wrap .form-control {
      color: #1f2937 !important;
      background-color: #fff !important;
      border: 1px solid #cbd5e1 !important;
    }
    .esx-wrap .form-control::placeholder {
      color: #9ca3af !important;
    }
    /* El tema pisa el estado :checked de los checkboxes nativos (se ve
       marcado y sin marcar exactamente igual). Se quita el render nativo
       y se dibuja el check a mano para que sí se note la selección. */
    .esx-wrap .form-check-input[type="checkbox"] {
      -webkit-appearance: none !important;
      appearance: none !important;
      width: 18px !important;
      height: 18px !important;
      min-width: 18px !important;
      border: 2px solid #94a3b8 !important;
      border-radius: 4px !important;
      background-color: #fff !important;
      background-image: none !important;
      box-shadow: none !important;
      position: relative;
      cursor: pointer;
      margin-top: 0.15em;
    }
    .esx-wrap .form-check-input[type="checkbox"]:checked {
      background-color: #2563eb !important;
      border-color: #2563eb !important;
    }
    .esx-wrap .form-check-input[type="checkbox"]:checked::after {
      content: "" !important;
      position: absolute !important;
      left: 5px !important;
      top: 1px !important;
      width: 5px !important;
      height: 10px !important;
      border: solid #fff !important;
      border-width: 0 2px 2px 0 !important;
      transform: rotate(45deg) !important;
    }
    .esx-wrap .form-check-input:disabled {
      opacity: 0.5 !important;
      cursor: not-allowed;
    }
    .esx-hint {
      font-size: 12px !important;
      color: #6b7280 !important;
      margin-top: 4px;
    }
    .esx-checklist {
      max-height: 220px;
      overflow-y: auto;
      border: 1px solid #cbd5e1 !important;
      border-radius: 8px;
      padding: 10px 12px;
      background: #fff !important;
    }
    .esx-checklist .form-check {
      margin-bottom: 4px;
    }
    .esx-badge-count {
      display: inline-block;
      background: #eef2ff !important;
      color: #4338ca !important;
      border-radius: 999px;
      padding: 2px 10px;
      font-size: 12px !important;
      font-weight: 600 !important;
      margin-left: 8px;
    }
    .esx-btn-exportar {
      min-width: 180px;
    }
  `}</style>
);

// Checklist reutilizable de selección múltiple (usado para Agencia, Status y Sub status).
// "todosSeleccionados" (lista vacía) = sin filtro = trae todo.
function ChecklistMultiple({
  titulo,
  opciones,
  seleccionados,
  onToggle,
  onSeleccionarTodos,
  disabled,
  conBuscador = true,
}) {
  const [busqueda, setBusqueda] = useState("");

  const opcionesFiltradas = useMemo(() => {
    const q = normalizarTexto(busqueda);
    if (!q) return opciones;
    return opciones.filter((valor) => normalizarTexto(etiquetaOpcion(valor)).includes(q));
  }, [opciones, busqueda]);

  const todosSeleccionados =
    seleccionados.length === 0 || seleccionados.length === opciones.length;

  return (
    <div className="esx-section">
      <div className="d-flex align-items-center justify-content-between esx-section-title">
        <span>{titulo}</span>
        {seleccionados.length > 0 && (
          <span className="esx-badge-count">{seleccionados.length} seleccionados</span>
        )}
      </div>

      {conBuscador && (
        <input
          className="form-control mb-2"
          placeholder={`Buscar ${titulo.toLowerCase()}...`}
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          disabled={disabled}
        />
      )}

      <div className="esx-checklist">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id={`chk-todos-${titulo}`}
            checked={todosSeleccionados}
            onChange={onSeleccionarTodos}
            disabled={disabled}
          />
          <label className="form-check-label fw-bold" htmlFor={`chk-todos-${titulo}`}>
            Todos
          </label>
        </div>
        <hr className="my-1" />
        {opcionesFiltradas.length === 0 && (
          <div className="esx-hint">Sin coincidencias.</div>
        )}
        {opcionesFiltradas.map((valor) => (
          <div className="form-check" key={valor}>
            <input
              className="form-check-input"
              type="checkbox"
              id={`chk-${titulo}-${valor}`}
              checked={seleccionados.includes(valor)}
              onChange={() => onToggle(valor)}
              disabled={disabled}
            />
            <label className="form-check-label" htmlFor={`chk-${titulo}-${valor}`}>
              {etiquetaOpcion(valor)}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

const BabiloniaExportSolicitudes = () => {
  const [filtros, setFiltros] = useState({
    folio_venta: "",
    venta_babilonia: "",
    status: [], // vacío = todos (se filtra en cliente, normalizado como el dashboard)
    sub_status: [], // vacío = todos (idem)
    departamento: [], // vacío = todos (idem)
    tipoPago: [], // vacío = todos (idem)
    agencia: [], // vacío = todas
    startDate: "",
    endDate: "",
    soloFormalizadas: false, // mismo criterio que "Formalizadas" del dashboard
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalExportado, setTotalExportado] = useState(null);

  const [opciones, setOpciones] = useState({
    venta_babilonia: [],
    agencia: [],
  });
  const [opcionesLoading, setOpcionesLoading] = useState(true);
  const [opcionesError, setOpcionesError] = useState("");

  useEffect(() => {
    const cargarOpciones = async () => {
      setOpcionesLoading(true);
      setOpcionesError("");
      try {
        const body = new URLSearchParams({ access_token: ACCESS_TOKEN });
        const resp = await fetch(`${API_BASE_URL}ventas/vehiculo/consultar2`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body,
        });
        const json = await resp.json();

        if (json.statusCode !== 200 || !json.data?.opciones) {
          setOpcionesError("No se pudieron cargar las opciones de filtros.");
          return;
        }

        setOpciones({
          venta_babilonia: limpiarOpciones(json.data.opciones.venta_babilonia),
          agencia: limpiarOpciones(json.data.opciones.agencia),
        });
      } catch (e) {
        setOpcionesError("Error al cargar opciones de filtros: " + e.message);
      } finally {
        setOpcionesLoading(false);
      }
    };

    cargarOpciones();
  }, []);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFiltros({ ...filtros, [name]: type === "checkbox" ? checked : value });
  };

  // Toggle genérico para cualquier filtro de selección múltiple (agencia, status, sub_status)
  const toggleValor = (campo, valor) => {
    setFiltros((prev) => {
      const lista = prev[campo];
      const yaEsta = lista.includes(valor);
      const nuevaLista = yaEsta ? lista.filter((v) => v !== valor) : [...lista, valor];
      return { ...prev, [campo]: nuevaLista };
    });
  };

  // "Todos": un clic limpia la selección (vacío = sin filtro = todos)
  const seleccionarTodos = (campo) => {
    setFiltros((prev) => ({ ...prev, [campo]: [] }));
  };

  const handleExportar = async () => {
    setLoading(true);
    setError("");
    setTotalExportado(null);
    try {
      // Filtros que sí van al SQL (activo se fuerza siempre en el backend)
      const CAMPOS_BACKEND = ["folio_venta", "venta_babilonia", "agencia", "startDate", "endDate"];

      const body = new URLSearchParams();
      body.append("access_token", ACCESS_TOKEN);

      CAMPOS_BACKEND.forEach((key) => {
        const value = filtros[key];
        if (Array.isArray(value)) {
          if (value.length > 0) {
            value.forEach((v) => body.append(`${key}[]`, v));
          }
          return;
        }
        if (value !== "" && value !== null) {
          body.append(key, value);
        }
      });

      const resp = await fetch(`${API_BASE_URL}ventas/vehiculo/consultar2`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const json = await resp.json();

      if (json.statusCode !== 200 || !json.data?.resultados) {
        setError(json.error?.description || "No se encontraron ventas con esos filtros.");
        setLoading(false);
        return;
      }

      let resultados = json.data.resultados;

      // Status / Sub status / Departamento / Tipo de pago: se filtran aquí, sobre el
      // valor NORMALIZADO (igual que el dashboard homologa status/sub_status/tipo_auto/
      // depende_auto antes de compararlos), no contra el texto crudo de la BD.
      if (filtros.status.length > 0) {
        resultados = resultados.filter((r) =>
          filtros.status.includes(normalizeStatusDash(r.status))
        );
      }
      if (filtros.sub_status.length > 0) {
        resultados = resultados.filter((r) =>
          filtros.sub_status.includes(normalizeSubStatusDash(r.sub_status))
        );
      }
      if (filtros.departamento.length > 0) {
        resultados = resultados.filter((r) =>
          filtros.departamento.includes(normalizeDepartamentoDash(r.tipo_auto))
        );
      }
      if (filtros.tipoPago.length > 0) {
        resultados = resultados.filter((r) =>
          filtros.tipoPago.includes(normalizeTipoPagoDash(r.depende_auto))
        );
      }

      if (filtros.soloFormalizadas) {
        resultados = resultados.filter(esFormalizadaComoDashboard);
      }

      const filas = resultados.map(mapVentaAFila);

      const ws = XLSX.utils.json_to_sheet(filas, { header: COLUMNAS });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Hoja1");

      const nombreArchivo = `Solicitudes_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, nombreArchivo);

      setTotalExportado(filas.length);
    } catch (e) {
      setError("Error al exportar: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 esx-wrap">
      <EstilosExport />

      <h4 className="mb-1">Exportar solicitudes a Excel</h4>
      <p className="esx-hint mb-3">
        Este export siempre está acotado a solicitudes con Activo =
        "Completado" (igual que el dashboard). Status, Sub status,
        Departamento y Tipo de pago se comparan ya homologados (igual que el
        dashboard homologa typos/variantes a sus categorías fijas antes de
        contar). Los demás filtros son opcionales; deja "Todos"/"Todas"
        (lista vacía) para no aplicarlos.
      </p>

      {opcionesError && (
        <div className="alert alert-warning py-2">{opcionesError}</div>
      )}

      {/* Búsqueda puntual */}
      <div className="esx-section">
        <div className="esx-section-title">Búsqueda</div>
        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label mb-1">Folio de venta</label>
            <input
              className="form-control"
              name="folio_venta"
              placeholder="Ej. VF-000123"
              value={filtros.folio_venta}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label mb-1">Desde</label>
            <input
              type="date"
              className="form-control"
              name="startDate"
              value={filtros.startDate}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label mb-1">Hasta</label>
            <input
              type="date"
              className="form-control"
              name="endDate"
              value={filtros.endDate}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      {/* Venta Babilonia (un solo valor) + Solo Formalizadas */}
      <div className="esx-section">
        <div className="esx-section-title">Estatus general</div>
        <div className="row g-2 mb-2">
          <div className="col-md-6">
            <label className="form-label mb-1">Venta Babilonia</label>
            <select
              className="form-control"
              name="venta_babilonia"
              value={filtros.venta_babilonia}
              onChange={handleChange}
              disabled={opcionesLoading}
            >
              <option value="">Todos</option>
              {opciones.venta_babilonia.map((valor) => (
                <option key={valor} value={valor}>
                  {valor}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-check mt-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="soloFormalizadas"
            name="soloFormalizadas"
            checked={filtros.soloFormalizadas}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="soloFormalizadas">
            Solo Formalizadas
          </label>
          <div className="esx-hint">
            Mismo criterio que el dashboard: Crédito por sub_status, Contado/Arrendamiento por status.
          </div>
        </div>
      </div>

      {/* Status - selección múltiple, contra categorías normalizadas (igual que el dashboard) */}
      <ChecklistMultiple
        titulo="Status"
        opciones={CANONICAL_STATUS}
        seleccionados={filtros.status}
        onToggle={(valor) => toggleValor("status", valor)}
        onSeleccionarTodos={() => seleccionarTodos("status")}
        disabled={false}
        conBuscador={false}
      />

      {/* Sub status - selección múltiple, contra categorías normalizadas */}
      <ChecklistMultiple
        titulo="Sub status"
        opciones={CANONICAL_SUB_STATUS}
        seleccionados={filtros.sub_status}
        onToggle={(valor) => toggleValor("sub_status", valor)}
        onSeleccionarTodos={() => seleccionarTodos("sub_status")}
        disabled={false}
        conBuscador={false}
      />

      {/* Departamento - selección múltiple, contra categorías normalizadas (tipo_auto) */}
      <ChecklistMultiple
        titulo="Departamento"
        opciones={CANONICAL_DEPARTAMENTO}
        seleccionados={filtros.departamento}
        onToggle={(valor) => toggleValor("departamento", valor)}
        onSeleccionarTodos={() => seleccionarTodos("departamento")}
        disabled={false}
        conBuscador={false}
      />

      {/* Tipo de pago - selección múltiple, contra categorías normalizadas (depende_auto) */}
      <ChecklistMultiple
        titulo="Tipo de pago"
        opciones={CANONICAL_TIPO_PAGO}
        seleccionados={filtros.tipoPago}
        onToggle={(valor) => toggleValor("tipoPago", valor)}
        onSeleccionarTodos={() => seleccionarTodos("tipoPago")}
        disabled={false}
        conBuscador={false}
      />

      {/* Agencias - selección múltiple (con buscador, la lista puede ser larga) */}
      <ChecklistMultiple
        titulo="Agencias"
        opciones={opciones.agencia}
        seleccionados={filtros.agencia}
        onToggle={(valor) => toggleValor("agencia", valor)}
        onSeleccionarTodos={() => seleccionarTodos("agencia")}
        disabled={opcionesLoading}
        conBuscador={true}
      />

      {error && <div className="alert alert-danger">{error}</div>}
      {totalExportado !== null && !error && (
        <div className="alert alert-success">
          Se exportaron {totalExportado} registros correctamente.
        </div>
      )}

      <button
        className="btn btn-primary esx-btn-exportar"
        onClick={handleExportar}
        disabled={loading}
      >
        {loading ? "Exportando..." : "Exportar a Excel"}
      </button>
    </div>
  );
};

export default BabiloniaExportSolicitudes;